import React from 'react';
import { cn } from '../utils/cn';

/**
 * Card Component - Atom
 * 
 * @param {Object} props
 * @param {string} props.variant - 'default' | 'outlined' | 'elevated'
 * @param {boolean} props.hover - Hover effect
 */
const Card = React.forwardRef(
  (
    {
      variant = 'default',
      hover = false,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const baseStyles = 'bg-white dark:bg-primary-800 rounded-xl transition-all duration-200 ease-out';

    const variants = {
      default: 'border border-primary-200 dark:border-primary-700',
      outlined: 'border-2 border-primary-300 dark:border-primary-600',
      elevated: 'border border-primary-200 dark:border-primary-700',
    };

    // Check if className already has padding (p-, px-, py-, pt-, pb-, pl-, pr-)
    const hasPadding = /p[tblrxy]?-\d/.test(className);

    // Level 2 shadows for charts and large widgets - softer than Level 1
    const shadowClasses = {
      default: 'shadow-[0_1px_3px_0_rgba(0,0,0,0.05),0_1px_2px_-1px_rgba(0,0,0,0.05)] dark:shadow-[0_1px_3px_0_rgba(0,0,0,0.3),0_1px_2px_-1px_rgba(0,0,0,0.2)]',
      outlined: 'shadow-[0_1px_3px_0_rgba(0,0,0,0.05),0_1px_2px_-1px_rgba(0,0,0,0.05)] dark:shadow-[0_1px_3px_0_rgba(0,0,0,0.3),0_1px_2px_-1px_rgba(0,0,0,0.2)]',
      elevated: 'shadow-[0_10px_15px_-3px_rgba(0,0,0,0.08),0_4px_6px_-4px_rgba(0,0,0,0.08)] dark:shadow-[0_10px_15px_-3px_rgba(0,0,0,0.5),0_4px_6px_-4px_rgba(0,0,0,0.4)]',
    };

    const hoverShadowClasses = {
      default: 'hover:shadow-[0_4px_6px_-1px_rgba(0,0,0,0.06),0_2px_4px_-2px_rgba(0,0,0,0.06)] dark:hover:shadow-[0_4px_6px_-1px_rgba(0,0,0,0.4),0_2px_4px_-2px_rgba(0,0,0,0.3)]',
      outlined: 'hover:shadow-[0_4px_6px_-1px_rgba(0,0,0,0.06),0_2px_4px_-2px_rgba(0,0,0,0.06)] dark:hover:shadow-[0_4px_6px_-1px_rgba(0,0,0,0.4),0_2px_4px_-2px_rgba(0,0,0,0.3)]',
      elevated: 'hover:shadow-[0_20px_25px_-5px_rgba(0,0,0,0.1),0_8px_10px_-6px_rgba(0,0,0,0.1)] dark:hover:shadow-[0_20px_25px_-5px_rgba(0,0,0,0.6),0_8px_10px_-6px_rgba(0,0,0,0.5)]',
    };

    return (
      <div
        ref={ref}
        className={cn(
          baseStyles,
          variants[variant],
          shadowClasses[variant],
          !hasPadding && 'p-6',
          hover && hoverShadowClasses[variant],
          hover && 'hover:border-primary-300 dark:hover:border-primary-600 hover:-translate-y-0.5',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

export default Card;
